"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef, useState } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { hasAppAccess } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { syncServerLanguage } from "@/lib/serverLanguage"
import {
  isPrivacyDataPath,
  signalHealthConsentRequired,
  useHealthConsentRequired,
} from "@/lib/healthConsentGate"
import { LoadingState, ErrorState } from "@/components/state"
import { HealthConsentGate } from "@/components/consent/HealthConsentGate"

type Check = "loading" | "complete" | "incomplete" | "noAccess" | "noConsent" | "error"

/**
 * Layout-route guard for /app/* feature screens: redirects users whose
 * onboarding isn't `completed` (per `GET /v1/me`) to /app/onboarding
 * (T11 "add the guard"). Intentionally separate from
 * `components/auth/RequireAuth` (out of this task's edit scope) — this
 * guard only concerns onboarding completion, not authentication, and
 * assumes an authenticated session already exists by the time it renders.
 *
 * Bug 2 fix (mirrors mobile's A4 fix in mobile/src/app/index.tsx): a
 * failed /v1/me used to fall through to rendering the protected Outlet —
 * `/v1/me` is the ONLY thing that creates a `users` row, so a signed-in
 * user with no row (or any transient failure) would silently land on
 * Today instead of onboarding, with no visible error and no way to
 * retry. Now a failure renders an explicit error state and retrying
 * re-runs the /v1/me check via the `meAttempt` counter.
 *
 * Item 8 step 7 (2026-09-12): a completed onboarding no longer implies
 * entry into the rest of /app/* — `/app/access` sits INSIDE this guard
 * (see routes/index.tsx) precisely so this same check can also redirect
 * an unentitled user there instead of adding a second nested guard (which
 * would mean a second /v1/me fetch and a second loading flash). The one
 * exception: a user already ON /app/access is never redirected to
 * itself — `hasAppAccess` false there just means "show the claim/subscribe
 * screen", exactly what a fresh completion of onboarding needs.
 *
 * Health/AI consent (launch compliance, 2026-10-14): a COMPLETED account without an
 * active `healthAiConsent` (every account that finished onboarding before the consent
 * existed) gets the consent screen as a blocking gate on its next app start — the
 * protected Outlet (Today, Plan, Kitchen, Shop, More) never mounts. The same gate
 * appears when any endpoint answers 403 HEALTH_AI_CONSENT_REQUIRED mid-session
 * (lib/healthConsentGate.ts). A response without the field reads as no consent.
 *
 * ONE route is exempt: Privacy & data (export, deletion, how to withdraw). The server allows those without
 * consent, so a person who does not consent must still reach them: on that route the guard renders the
 * page — neither the consent gate nor the unfinished-onboarding redirect applies while the consent is
 * missing — and raises the signal so AppShell shows it in its stripped frame (no Amy, no nav).
 */
export function RequireOnboardingComplete() {
  const api = useApiClient()
  const { locale } = useI18n()
  const location = useLocation()
  const [check, setCheck] = useState<Check>("loading")
  const [meAttempt, setMeAttempt] = useState(0)
  // Whether the last /v1/me said the health/AI consent is missing (for the Privacy & data exemption).
  const [consentMissing, setConsentMissing] = useState(false)
  const pathRef = useRef(location.pathname)
  pathRef.current = location.pathname

  useEffect(() => {
    let cancelled = false
    setCheck("loading")
    api
      .getMe()
      .then(async me => {
        if (cancelled) return
        // Server language reconciliation (2026-09-10). `users.locale` decides
        // which language the API answers catalogue names in, and until now only
        // onboarding ever wrote it — so an account created in EN and later
        // switched to DE kept receiving English ingredient names. This runs
        // BEFORE the protected Outlet renders, so pages never fetch into the
        // stale language and there is no refetch race. A failed write is
        // swallowed: it must never keep a signed-in user out of the app.
        if (me.locale !== locale) {
          try {
            await api.patchMe({ locale })
          } catch {
            /* names stay as the server has them until the next attempt */
          }
        }
        if (cancelled) return
        setConsentMissing(!me.healthAiConsent)
        // On Privacy & data the stripped frame (no Amy, no nav into health surfaces) applies to anyone
        // without consent — also to an account still in onboarding, which has the consent interstitial.
        if (!me.healthAiConsent && isPrivacyDataPath(pathRef.current)) signalHealthConsentRequired()
        if (me.onboardingStatus !== "completed") {
          setCheck("incomplete")
          return
        }
        if (!me.healthAiConsent) {
          signalHealthConsentRequired()
          setCheck("noConsent")
          return
        }
        setCheck(hasAppAccess(me) ? "complete" : "noAccess")
      })
      .catch(() => {
        if (!cancelled) setCheck("error")
      })
    return () => {
      cancelled = true
    }
    // `locale` is deliberately NOT a dependency: this reconciles on ENTRY. A
    // switch while the app is open is handled by AppShell's effect + the
    // serverLanguage notifier, which does not blank the app behind a spinner.
    // Deliberately narrow dependency list.
  }, [api, meAttempt])

  // A switch made while the app is open: write it through, then let the
  // catalogue surfaces re-read. Kept separate from the entry reconciliation
  // above so a language change never blanks the app behind a spinner. The ref
  // starts at the entry language, so the first render writes nothing.
  const writtenLocaleRef = useRef(locale)
  useEffect(() => {
    if (writtenLocaleRef.current === locale) return
    writtenLocaleRef.current = locale
    void syncServerLanguage(api, locale)
  }, [api, locale])

  // A 403 HEALTH_AI_CONSENT_REQUIRED from any endpoint while the app is open (consent withdrawn, stale
  // session): the same gate, never a generic error.
  const consentSignalled = useHealthConsentRequired()
  if (
    isPrivacyDataPath(location.pathname) &&
    (consentMissing || consentSignalled) &&
    check !== "loading" &&
    check !== "error"
  ) {
    return <Outlet />
  }
  if (check === "noConsent" || (consentSignalled && check !== "loading" && check !== "error" && check !== "incomplete")) {
    return <HealthConsentGate onConsented={() => setMeAttempt(n => n + 1)} />
  }

  if (check === "loading") {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingState />
      </div>
    )
  }

  if (check === "error") {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <ErrorState onRetry={() => setMeAttempt(n => n + 1)} />
      </div>
    )
  }

  if (check === "incomplete") {
    return <Navigate to="/app/onboarding" replace />
  }

  if (check === "noAccess" && location.pathname !== "/app/access") {
    return <Navigate to="/app/access" replace />
  }

  return <Outlet />
}
